import { useEffect, useRef } from 'react';
import { Box, Button, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-kml';
import JSZip from 'jszip';

export default function Recorrido() {
    const mapInstanceRef = useRef(null);
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');

    const handleVerConvocatoria = () => {
        window.open("/pliego.pdf", '_blank');
    };

    const handleParticipar = () => {
        // Navegar al formulario de participar
        navigate('/recorrido/participar');
    };

    useEffect(() => {
        // Crear el mapa solo si no existe
        if (!mapInstanceRef.current) {
            // Coordenadas por defecto de Villa María
            const defaultCenter = [-32.41469454047811, -63.2423299238929];
            const defaultZoom = 13;

            const map = L.map('recorrido-map', {
                center: defaultCenter,
                zoom: defaultZoom,
                zoomControl: true,
            });

            // Agregar capa de tiles de Google
            L.tileLayer('http://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
                attribution: 'Map data ©2024 Google',
                preferCanvas: true,
                keepBuffer: 10,
            }).addTo(map);

            mapInstanceRef.current = map;

            // Cargar archivo KMZ
            fetch('/recorrido.kmz')
                .then(res => {
                    if (!res.ok) {
                        throw new Error(`HTTP error! status: ${res.status}`);
                    }
                    return res.arrayBuffer();
                })
                .then(arrayBuffer => {
                    const zip = new JSZip();
                    return zip.loadAsync(arrayBuffer);
                })
                .then(zip => {
                    const kmlFile = zip.file(/\.kml$/i)[0];
                    if (!kmlFile) {
                        throw new Error('No KML file found in KMZ');
                    }
                    return kmlFile.async('text');
                })
                .then(kmlText => {
                    const parser = new DOMParser();
                    const kml = parser.parseFromString(kmlText, 'text/xml');
                    
                    // Verificar errores de parsing
                    const parseError = kml.querySelector('parsererror');
                    if (parseError) {
                        throw new Error('Error parsing KML: ' + parseError.textContent);
                    }

                    const kmlLayer = new L.KML(kml);
                    const bounds = kmlLayer.getBounds();
                    
                    if (bounds.isValid()) {
                        map.addLayer(kmlLayer);
                        map.fitBounds(bounds, { padding: [50, 50] });
                    } else {
                        console.warn('Invalid bounds for KML layer, using default view');
                    }
                })
                .catch(e => {
                    console.error('Error loading KMZ:', e);
                });
        }

        // Ajustar el tamaño del mapa cuando cambie el tamaño de la ventana
        const handleResize = () => {
            if (mapInstanceRef.current) {
                setTimeout(() => {
                    mapInstanceRef.current.invalidateSize();
                }, 100);
            }
        };

        window.addEventListener('resize', handleResize);

        // Cleanup al desmontar
        return () => {
            window.removeEventListener('resize', handleResize);
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    return (
        <Box
            sx={{
                width: '100%',
                height: 'calc(100vh - 80px)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div 
                id="recorrido-map" 
                style={{ 
                    height: '100%', 
                    width: '100%',
                    zIndex: 0,
                }} 
            />
            
            {/* Botones flotantes */}
            <Box
                sx={{
                    position: 'absolute',
                    top: { xs: '20px', sm: '30px', md: '40px' },
                    right: { xs: '20px', sm: '30px', md: '40px' },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: '12px', sm: '16px' },
                    zIndex: 1000,
                }}
            >
                <Button
                    variant="contained"
                    onClick={handleVerConvocatoria}
                    sx={{
                        backgroundColor: '#220015',
                        backdropFilter: 'blur(10px)',
                        color: '#FFFFFF',
                        borderRadius: '8px',
                        px: { xs: '16px', sm: '20px' },
                        py: { xs: '10px', sm: '12px' },
                        fontSize: { xs: '14px', sm: '16px' },
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: '#2d001f',
                            transform: 'scale(1.05)',
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                        },
                    }}
                >
                    Ver Convocatoria
                </Button>
                <Button
                    variant="contained"
                    onClick={handleParticipar}
                    sx={{
                        backgroundColor: '#D4A646',
                        backdropFilter: 'blur(10px)',
                        color: '#FFFFFF',
                        borderRadius: '8px',
                        px: { xs: '16px', sm: '20px' },
                        py: { xs: '10px', sm: '12px' },
                        fontSize: { xs: '14px', sm: '16px' },
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: '#C4962F',
                            transform: 'scale(1.05)',
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                        },
                    }}
                >
                    Participar
                </Button>
            </Box>
        </Box>
    );
}

